import { useEffect, useState, ReactNode } from 'react'
import {
  getURLMetadata,
  IframelyError,
  IframelyData,
} from 'components/Handbook2_0/Sidebar/HandbookItemTypes/getURLMetadata'
import { DEFAULT_DOC_TITLE } from 'utils/constants'
import { FileType, ValidationResult, getFileTypeFrom, LOADING_TITLE, FILE_NAME, FILE_ICON } from './urlValidationUtils'
import { VALIDATE_URL_FOR } from './Validators/validateUrlFor'

interface useValidateFileUrlProps {
  onIconChange?: (icon: FileType) => void
  onTitleChange?: (title: string) => void
}

export const useValidateFileUrl = ({ onTitleChange, onIconChange }: useValidateFileUrlProps) => {
  const [isValid, setIsValid] = useState(false)
  const [isEmbeddable, setIsEmbeddable] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [fileType, setFileType] = useState<FileType | null>(null)
  const [fileSubType, setFileSubType] = useState<FileType | null>(null)
  const [frontIcon, setFrontIcon] = useState<ReactNode | null>(null)

  const fetchFileMetadata = async (externalLink: string, fileType: FileType) => {
    setIsLoading(true)
    if (!fileType || !externalLink) return

    onTitleChange?.(LOADING_TITLE)
    let url = new URL(externalLink)

    // For Figma, iframely only accepts the raw url without all the embed stuff
    if (fileType === FileType.FIGMA)
      url.href = url.href.replace('https://www.figma.com/embed?embed_host=share&url=', '')

    const response = await getURLMetadata(url.href)

    const urlData = response as IframelyData
    onTitleChange?.(
      urlData?.meta?.title ||
        (fileSubType && FILE_NAME[fileSubType]) ||
        (fileType && FILE_NAME[fileType]) ||
        DEFAULT_DOC_TITLE,
    )

    // If Iframely managed to fetch data and it's from drive, then set its sub type
    if (urlData?.meta) {
      if (fileType === FileType.FILE) {
        if (urlData.meta.medium === FileType.FILE) {
          setFileSubType(FileType.PDF)
        } else {
          setFileSubType(FileType.IMAGES)
        }
      }

      setIsLoading(false)
      return
    }

    // Some caveats: even if iframely failed to fetch data, we can still embed the link in some cases
    const urlError = response as IframelyError
    if (fileType === FileType.FILE) {
      // If file is from Google Drive and error is 403, it was a PDF and it's embeddable,
      if (urlError.status === 403 && !urlError.error.includes('The page is private and requires a login.')) {
        setFileSubType(FileType.PDF)
        setIsLoading(false)
        return
      }

      // Otherwise it's not embeddable, set fileSubType to null so that Google Drive's icon is used
      setFileSubType(null)
    }

    // If file is from Dropbox and error is 417, it was an Image and it's embeddable
    if (fileType === FileType.DROPBOX) {
      if (urlError.status === 417) {
        setIsLoading(false)
        return
      }

      // Otherwise it's not embeddable, set fileSubType to null so that Dropbox's icon is used
      setFileSubType(null)
    }

    // If file is from Figma and error is 403, it's embeddable
    if (
      fileType === FileType.FIGMA &&
      (urlError.status === 403 || urlError.error.includes('The page appears to be private'))
    ) {
      setIsLoading(false)
      return
    }

    // If file is a PDFs hosted outside of Dropbox/Google Drive, error is 417 if it's bigger than 10mb and it's embeddable
    if (fileType === FileType.PDF && urlError.status === 417) {
      setIsLoading(false)
      return
    }

    // As a last resort, if for any file type iframely returns a 417 error (it's not clear why it does in some cases),
    // we assume it can be embedded because the url has passed our validations
    //
    // Info on this error: https://iframely.com/docs/result-codes#417
    if (urlError.status === 417) {
      setIsLoading(false)
      return
    }

    setIsLoading(false)
    setIsEmbeddable(false)
  }

  // If 'fileSubType' is set, use it, otherwise use 'fileType'
  useEffect(() => {
    if (fileSubType) {
      setFrontIcon(FILE_ICON[fileSubType])
      onIconChange?.(fileSubType)

      return
    }

    if (fileType) {
      setFrontIcon(FILE_ICON[fileType])
      onIconChange?.(fileType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileType, fileSubType])

  const validateUrl = (url: string) => {
    if (url.length < 1) {
      setIsValid(false)
      return
    }

    const externalFileType = getFileTypeFrom(url) as FileType
    let result: ValidationResult = { validUrl: '', isEmbeddable: false }

    if (!externalFileType) {
      setFileType(null)
      setIsValid(false)
      return
    }
    setIsValid(true)

    result = VALIDATE_URL_FOR[externalFileType]?.(url)
    const validUrl = result?.validUrl || url
    const newFileType = result?.newFileType || externalFileType

    setIsEmbeddable(result?.isEmbeddable)
    setFileType(newFileType)
    setFileSubType(result?.newFileSubType || null)
    fetchFileMetadata(validUrl, newFileType)

    return validUrl
  }

  return {
    validateUrl,
    isValid,
    frontIcon,
    isEmbeddable,
    fileType,
    fileSubType,
    isLoading,
  }
}
